// PARTIAL EXTRACTION. The five descriptor TYPES and the two PURE parsers that
// used to be declared here now live in `@heidi/agents`, because the carved
// Routines mutations build their analytics payloads and normalise their input
// with them and a headless package may not import an app module. The emitters
// below stayed: they reach `trackWorkEvent`, and the run one probes the desktop
// RPA bridge.
//
// The names are re-exported name-intact, so every existing importer of this path
// is unaffected. History for the moved half:
// `git log --follow -- src/features/workflows/workflowAnalytics.ts`.
import { trackWorkEvent, WorkLaunchEventName } from '@/models/analytics/features/work';
import type {
  EventType,
  WorkflowAnalyticsProps,
  WorkflowInstallSource,
} from '@heidi/agents/contracts';
import { getClientCapabilities } from '@heidi/agents/data';

import type { Workflow } from '@heidi/agents/contracts';

export type {
  RunAnyWorkflowInput,
  WorkflowAnalyticsProps,
  WorkflowInstallSource,
  WorkflowTriggerSource,
  WorkflowTriggerType,
} from '@heidi/agents/contracts';
export { eventTypesLabel, parseRunAnyWorkflowInput } from '@heidi/agents/data';

export async function resolveClientCapabilitiesPresent(): Promise<boolean> {
  const capabilities = await getClientCapabilities().catch(() => null);
  return capabilities !== null;
}

export function trackWorkflowActioned(props: WorkflowAnalyticsProps): void {
  trackWorkEvent(WorkLaunchEventName.WorkWorkflowActioned, {
    surface: 'workflow',
    ...props,
    preset_id: props.preset_id ?? undefined,
  });
}

export async function trackWorkflowRunClicked(
  props: Pick<
    WorkflowAnalyticsProps,
    | 'workflow_id'
    | 'trigger_source'
    | 'trigger_type'
    | 'requires_desktop'
    | 'preset_id'
    | 'outcome'
  >
): Promise<void> {
  trackWorkflowActioned({
    action: 'run_clicked',
    object_type: 'workflow',
    client_capabilities_present: await resolveClientCapabilitiesPresent(),
    outcome: 'succeeded',
    ...props,
  });
}

export function trackWorkflowCreated(
  workflow: Pick<Workflow, 'workflow_id' | 'requires_desktop' | 'source_template_id'>,
  config: {
    has_schedule?: boolean;
    has_event_rules?: boolean;
    event_types?: EventType[];
    install_source?: WorkflowInstallSource;
  } = {}
): void {
  trackWorkflowActioned({
    action: 'created',
    object_type: 'workflow',
    workflow_id: workflow.workflow_id,
    preset_id: workflow.source_template_id,
    requires_desktop: workflow.requires_desktop,
    has_schedule: config.has_schedule,
    has_event_rules: config.has_event_rules,
    event_types: config.event_types?.join(',') || undefined,
    install_source: config.install_source,
  });
}

export function trackWorkflowInstalled(
  workflow: Pick<Workflow, 'workflow_id' | 'requires_desktop' | 'source_template_id'>,
  installSource: WorkflowInstallSource,
  presetId?: string,
  config?: {
    has_schedule?: boolean;
    has_event_rules?: boolean;
    event_types?: EventType[];
  }
): void {
  trackWorkflowActioned({
    action: 'installed',
    object_type: 'preset',
    workflow_id: workflow.workflow_id,
    preset_id: workflow.source_template_id ?? presetId,
    requires_desktop: workflow.requires_desktop,
    install_source: installSource,
    has_schedule: config?.has_schedule,
    has_event_rules: config?.has_event_rules,
    event_types: config?.event_types?.join(',') || undefined,
  });
}
